import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
  Table,
  Form,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaEye,
  FaTrash,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaCheckCircle,
  FaTimes,
  FaBuilding,
  FaCalendarAlt,
  FaGlobe,
} from "react-icons/fa";
import { AppDispatch, RootState } from "../../store";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import usersService from "../../features/users/usersService";
import { User, Role } from "../../features/users/usersSlice";
import UserFormModal from "../../components/users/UserFormModal";
import Swal from 'sweetalert2';
import Pagination from "../../components/common/Pagination";
import companiesService, { Company } from "../../features/companies/companiesService";
import { useTheme } from "../../context/ThemeContext";
import { isCompanyOwner } from "../../utils/permissions";

const MyTeamPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [agencyId, setAgencyId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);

  // Team members'ları yükle
  useEffect(() => {
    loadOwnerContext();
    loadRoles();
  }, []);

  useEffect(() => {
    if (agencyId || companyId) {
      loadTeamMembers();
    }
  }, [agencyId, companyId]);

  const loadOwnerContext = async () => {
    try {
      if (isCompanyOwner(user)) {
        const myCompany = await companiesService.getMyCompany();
        setCompanyId(myCompany.id);
        setCompanies([myCompany as Company]);
      } else {
        // Agency Owner context
        const allCompanies = await companiesService.getMyAgencyCompanies();
        const agency = allCompanies.find((company: any) => company.company_type === "agency");
        if (agency) {
          setAgencyId(agency.id);
          setCompanies(allCompanies);
        }
      }
    } catch (err) {
      console.error("Error loading owner context:", err);
      setError("Failed to load owner context. Please try again.");
    }
  };

  const loadTeamMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (isCompanyOwner(user)) {
        if (!companyId) return;
        const members = await companiesService.getCompanyUsers(companyId);
        // company_owner da listede görünsün; id alanını normalize et
        const filtered = (members || [])
          .filter((m: any) =>
            Array.isArray(m.roles) && m.roles.some((r: any) => {
              const key = r.key || r;
              return key === "company_specialist" || key === "company_owner";
            })
          )
          .map((m: any) => ({ ...m, id: m.user_id }));
        setTeamMembers(filtered);
      } else {
        if (!agencyId) return;
        const members = await usersService.getAgencyUsers(agencyId);
        setTeamMembers(members);
      }
    } catch (err) {
      console.error("Error loading team members:", err);
      setError("Failed to load team members. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const allRoles = await usersService.getRoles();
      let relevantRoles: Role[] = [];
      if (isCompanyOwner(user)) {
        relevantRoles = allRoles.filter((role: Role) => role.key === "company_specialist");
      } else {
        relevantRoles = allRoles.filter((role: Role) => 
          role.key === "marketing_specialist" || role.key === "agency_owner"
        );
      }
      setRoles(relevantRoles);
    } catch (err) {
      console.error("Error loading roles:", err);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadTeamMembers();
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    loadTeamMembers();
  };

  const handleModalHide = (refresh?: boolean) => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setSelectedUser(null);
    if (refresh) {
      loadTeamMembers();
    }
  };

  const handleViewUser = async (usr: User) => {
    try {
      let userDetail: any;
      if (isCompanyOwner(user)) {
        userDetail = await usersService.getUserById(usr.id);
      } else {
        if (!agencyId) return;
        userDetail = await usersService.getAgencyUserDetail(agencyId, usr.id);
      }
      setSelectedUser(userDetail);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Error loading user details:", err);
      toast.error("Failed to load user details");
    }
  };

  const handleEditUser = async (usr: User) => {
    try {
      let userDetail: any;
      if (isCompanyOwner(user)) {
        userDetail = await usersService.getUserById(usr.id);
      } else {
        if (!agencyId) return;
        userDetail = await usersService.getAgencyUserDetail(agencyId, usr.id);
      }
      setSelectedUser(userDetail);
      setShowEditModal(true);
    } catch (err) {
      console.error("Error loading user details:", err);
      toast.error("Failed to load user details");
    }
  };

  const handleDeleteUser = async (usr: User) => {
    const result = await Swal.fire({
      title: 'Delete Team Member',
      text: `Are you sure you want to delete ${usr.full_name || usr.username}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Loading göster
      Swal.fire({
        title: 'Deleting User...',
        text: 'Please wait while we delete the user.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      if (isCompanyOwner(user)) {
        await usersService.deleteUser(usr.id);
      } else {
        if (!agencyId) return;
        await usersService.deleteAgencyUser(agencyId, usr.id);
      }
      loadTeamMembers();
      
      // Başarı mesajı göster
      Swal.fire({
        title: 'Success!',
        text: `${usr.full_name || usr.username} has been deleted successfully.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Error deleting user:", err);
      
      // Hata mesajı göster
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete user. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const getStatusBadge = (status: string) => {
    return status === "active" ? (
      <Badge bg="success" className="status-badge">
        <Icon icon={FaCheckCircle} className="me-1" />
        Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="status-badge">
        <Icon icon={FaTimes} className="me-1" />
        Inactive
      </Badge>
    );
  };

  const getRoleBadge = (roles: any[]) => {
    return (roles || []).map((r: any, index: number) => {
      const key: string = (r?.key || r || '').toString();
      const label: string = r?.name || key;
      const variantClass = key ? `role-${key.replace(/[^a-z0-9_-]/gi, '').toLowerCase()}` : '';
      return (
        <span key={index} className={`role-badge ${variantClass} me-1`}> {label} </span>
      );
    });
  };

  // Pagination
  const paginatedMembers = teamMembers.slice((page - 1) * limit, page * limit);
  const totalCount = teamMembers.length;
  const activeCount = teamMembers.filter((m) => m.status === "active").length;
  const inactiveCount = totalCount - activeCount;
  const totalPages = Math.ceil(totalCount / limit);

  if (isLoading) {
    return (
      <div className="my-team-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <Spinner animation="border" variant="primary" />
          </div>
          <p className="loading-text">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="py-4 my-team-page">
      <style>{`
        .my-team-page { background: ${isDark ? "#0f172a" : "#f8f9fa"}; }
        .page-header {
          background: ${isDark ? "#1e293b" : "#ffffff"};
          border: 1px solid ${isDark ? "#334155" : "#e9ecef"};
          box-shadow: ${isDark ? "0 2px 10px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0, 0, 0, 0.08)"};
        }
        .header-icon {
          background: ${isDark ? "rgba(59,130,246,0.15)" : "rgba(102,126,234,0.1)"};
          color: ${isDark ? "#60a5fa" : "#667eea"};
        }
        .page-title { color: ${isDark ? "#e2e8f0" : "#333"}; }
        .page-subtitle { color: ${isDark ? "#94a3b8" : "#666"}; }
        .filter-card, .content-card {
          background: ${isDark ? "#111827" : "#ffffff"};
          border: 1px solid ${isDark ? "#334155" : "#e9ecef"};
          box-shadow: ${isDark ? "0 2px 12px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0, 0, 0, 0.08)"};
        }
        .team-table th {
          background: ${isDark ? "#0b1220" : "#f8f9fa"};
          color: ${isDark ? "#e5e7eb" : "#495057"};
        }
        .team-table td { color: ${isDark ? "#e5e7eb" : "inherit"}; }
        .team-table tr:hover { background: ${isDark ? "rgba(59,130,246,0.08)" : "#f8f9fa"}; }
        .status-badge { color: #fff; }
        .empty-minimal { color: ${isDark ? "#94a3b8" : "#666"}; }
        .add-button { background: ${isDark ? "#3b82f6" : "#667eea"}; }
        .add-button:hover { background: ${isDark ? "#2563eb" : "#5a67d8"}; }
      `}</style>
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <Icon icon={FaUsers} />
            </div>
            <div className="header-text">
              <h1 className="page-title">{t("my_team")}</h1>
              <p className="page-subtitle">
                {totalCount} {t("total")} • {activeCount} {t("active")}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            className="add-button"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon icon={FaPlus} className="me-2" />
            {isCompanyOwner(user) ? (t("add_company_specialist") || "Add Company Specialist") : t("add_marketing_specialist")}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter and Status Badges */}
      <Card className="filter-card mb-3">
        <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <Badge bg="success" className="status-badge">
              {activeCount} {t("active")}
            </Badge>
            <Badge bg="secondary" className="status-badge">
              {inactiveCount} {t("inactive")}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              style={{ width: 80 }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
        </Card.Body>
      </Card>

      {/* Content */}
      <Card className="content-card">
        <Card.Body className="p-0">
          {paginatedMembers.length === 0 ? (
            <div className="empty-minimal text-center">
              <span className="text-muted small">{t("no_team_members")}</span>
            </div>
          ) : (
            <Table responsive className="team-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: 60 }}></th>
                  <th>{t("name")}</th>
                  <th>{t("email")}</th>
                  <th>{t("roles")}</th>
                  <th>{t("status")}</th>
                  <th style={{ width: 120 }}>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="user-avatar">
                        <Icon icon={FaUser} />
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold">{member.full_name || member.username}</div>
                      <div className="text-muted small">@{member.username}</div>
                    </td>
                    <td>
                      <a href={`mailto:${member.email}`} className="text-decoration-none">
                        {member.email}
                      </a>
                    </td>
                    <td>
                      {getRoleBadge(member.roles)}
                    </td>
                    <td>
                      {getStatusBadge(member.status)}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewUser(member)}
                          title={t("view_details")}
                        >
                          <Icon icon={FaEye} />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEditUser(member)}
                          title={t("edit_user")}
                        >
                          <Icon icon={FaEdit} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteUser(member)}
                          title="Delete User"
                        >
                          <Icon icon={FaTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {paginatedMembers.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            currentPage={page}
            totalCount={totalPages}
            pageSize={limit}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* User Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
        className="user-detail-modal"
      >
        <Modal.Header closeButton className="modal-header">
          <div className="header-bar w-100 d-flex align-items-center justify-content-between">
            <div className="header-left d-flex align-items-center gap-2">
              <div className="modal-icon d-none d-md-flex me-2">
                <Icon icon={FaUser} />
              </div>
              <div className="user-avatar-large me-2">
                <Icon icon={FaUser} />
              </div>
              <div className="identity d-flex flex-column">
                <div className="name fw-semibold">
                  {selectedUser?.full_name || selectedUser?.username || t("team_member")}
                </div>
                {selectedUser?.username && (
                  <div className="username text-white-50 small">@{selectedUser.username}</div>
                )}
              </div>
            </div>
            <div className="header-right d-flex align-items-center">
              {selectedUser && getStatusBadge(selectedUser.status)}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedUser && (
            <div className="user-detail">
              {/* Header info moved to modal header for a compact layout */}
              
              <div className="detail-section">
                <h6 className="mb-3">Contact Information</h6>
                <div className="detail-item">
                  <Icon icon={FaEnvelope} className="detail-icon" />
                  <a href={`mailto:${selectedUser.email}`} className="detail-link">
                    {selectedUser.email}
                  </a>
                </div>
              </div>
              
              <div className="detail-section">
                <h6 className="mb-3">Roles & Permissions</h6>
                <div className="detail-item">
                  <Icon icon={FaUsers} className="detail-icon" />
                  <span className="detail-text">{getRoleBadge(selectedUser.roles)}</span>
                </div>
              </div>
              
              <div className="detail-section">
                <h6 className="mb-3">Account Information</h6>
                <div className="detail-item">
                  <Icon icon={FaCalendarAlt} className="detail-icon" />
                  <span className="detail-text">
                    Created: {new Date(selectedUser.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="detail-item">
                  <Icon icon={FaGlobe} className="detail-icon" />
                  <span className="detail-text">
                    Status: {selectedUser.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <Button
            variant="secondary"
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowDetailModal(false);
              handleEditUser(selectedUser!);
            }}
          >
            Edit User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Form Modal */}
      <UserFormModal
        show={showCreateModal || showEditModal}
        onHide={handleModalHide}
        user={selectedUser}
        roles={roles}
        companies={companies}
        agencyId={agencyId || undefined}
      />
    </Container>
  );
};

export default MyTeamPage;

// CSS Styles
const styles = `
  /* Theme tokens for user detail modal */
  .user-detail-modal {
    --panel-bg: #ffffff;
    --body-bg: #f8f9fa;
    --border-color: #e9ecef;
    --text-primary: #1f2937;
    --text-muted: #6b7280;
    --accent: #667eea;
    --shadow: 0 22px 70px rgba(2, 6, 23, 0.18);
    --chip-bg: rgba(2, 6, 23, 0.04);
    --chip-border: rgba(2, 6, 23, 0.12);
  }
  [data-bs-theme="dark"] .user-detail-modal {
    --panel-bg: #0b1220;
    --body-bg: #0f172a;
    --border-color: #334155;
    --text-primary: #e5e7eb;
    --text-muted: #9ca3af;
    --accent: #8b5cf6;
    --shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
    --chip-bg: rgba(255, 255, 255, 0.06);
    --chip-border: rgba(255, 255, 255, 0.16);
  }
  .my-team-page {
    min-height: 100vh;
    background: #f8f9fa;
    padding: 20px 0;
  }

  .page-header {
    background: white;
    border-radius: 12px;
    margin-bottom: 24px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-info {
    display: flex;
    align-items: center;
  }

  .header-icon {
    background: rgba(102, 126, 234, 0.1);
    border-radius: 10px;
    padding: 12px;
    margin-right: 16px;
    color: #667eea;
    font-size: 20px;
  }

  .page-title {
    color: #333;
    margin: 0;
    font-weight: 600;
    font-size: 1.5rem;
    letter-spacing: -0.3px;
  }

  .page-subtitle {
    color: #666;
    margin: 4px 0 0 0;
    font-size: 0.875rem;
  }

  .add-button {
    background: #667eea;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-weight: 500;
    font-size: 0.875rem;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
    transition: all 0.2s ease;
  }

  .add-button:hover {
    background: #5a67d8;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  }

  .filter-card {
    background: white;
    border-radius: 10px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .content-card {
    background: white;
    border-radius: 12px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .team-table {
    margin: 0;
  }

  .team-table th {
    background: #f8f9fa;
    border: none;
    padding: 16px 12px;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
  }

  .team-table td {
    border: none;
    padding: 16px 12px;
    vertical-align: middle;
    font-size: 0.875rem;
  }

  .team-table tr:hover {
    background: #f8f9fa;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #667eea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 16px;
  }

  .user-avatar-large {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #667eea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
    margin-right: 16px;
  }

  .status-badge {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .role-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    background-color: var(--role-chip-bg, rgba(2, 6, 23, 0.04));
    color: var(--role-chip-text, #0f172a);
    border: 1px solid var(--role-chip-border, rgba(2, 6, 23, 0.12));
    letter-spacing: .2px;
  }

  /* Dark theme overrides for chips */
  [data-bs-theme="dark"] .role-badge {
    --role-chip-bg: rgba(255, 255, 255, 0.06);
    --role-chip-text: #e5e7eb;
    --role-chip-border: rgba(255, 255, 255, 0.16);
  }

  /* Role specific accents */
  .role-badge.role-marketing_specialist {
    --role-chip-bg: rgba(59, 130, 246, 0.10);
    --role-chip-text: #1d4ed8;
    --role-chip-border: rgba(59, 130, 246, 0.28);
  }
  [data-bs-theme="dark"] .role-badge.role-marketing_specialist {
    --role-chip-bg: rgba(59, 130, 246, 0.18);
    --role-chip-text: #bfdbfe;
    --role-chip-border: rgba(59, 130, 246, 0.35);
  }

  .role-badge.role-agency_owner {
    --role-chip-bg: rgba(16, 185, 129, 0.10);
    --role-chip-text: #047857;
    --role-chip-border: rgba(16, 185, 129, 0.28);
  }
  [data-bs-theme="dark"] .role-badge.role-agency_owner {
    --role-chip-bg: rgba(16, 185, 129, 0.18);
    --role-chip-text: #a7f3d0;
    --role-chip-border: rgba(16, 185, 129, 0.35);
  }

  .role-badge.role-company_owner {
    --role-chip-bg: rgba(99, 102, 241, 0.10);
    --role-chip-text: #3730a3;
    --role-chip-border: rgba(99, 102, 241, 0.28);
  }
  [data-bs-theme="dark"] .role-badge.role-company_owner {
    --role-chip-bg: rgba(99, 102, 241, 0.18);
    --role-chip-text: #c7d2fe;
    --role-chip-border: rgba(99, 102, 241, 0.35);
  }

  .role-badge.role-company_specialist {
    --role-chip-bg: rgba(245, 158, 11, 0.10);
    --role-chip-text: #b45309;
    --role-chip-border: rgba(245, 158, 11, 0.28);
  }
  [data-bs-theme="dark"] .role-badge.role-company_specialist {
    --role-chip-bg: rgba(245, 158, 11, 0.18);
    --role-chip-text: #fde68a;
    --role-chip-border: rgba(245, 158, 11, 0.35);
  }

  .empty-state {
    text-align: center;
    padding: 48px 20px;
    color: #666;
  }

  .empty-minimal {
    padding: 24px;
    color: #6c757d;
  }

  .empty-icon {
    margin-bottom: 16px;
    color: #ccc;
  }

  .empty-state h4 {
    margin-bottom: 8px;
    color: #333;
    font-size: 1.125rem;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }

  .loading-spinner {
    margin-bottom: 16px;
  }

  .loading-text {
    color: #666;
    font-size: 1rem;
  }

  /* Modal Styles */
  .modal-header {
    background: #667eea;
    color: white;
    border-bottom: none;
  }

  .modal-footer {
    background: #f8f9fa;
    border-top: none;
  }

  /* User Detail Modal structure */
  .user-detail-modal .modal-content {
    background: var(--panel-bg);
    border: 1px solid var(--border-color);
    border-radius: 16px;
    box-shadow: var(--shadow);
  }

  .user-detail-modal .modal-body {
    background: var(--body-bg);
  }

  .user-detail-modal .modal-footer {
    background: var(--panel-bg);
    border-top: 1px solid var(--border-color);
  }

  /* User Detail Modal header overrides */
  .user-detail-modal .modal-header {
    background: linear-gradient(135deg, var(--accent) 0%, #4f46e5 100%);
    color: #ffffff;
    border-bottom: none;
  }
  .user-detail-modal .modal-title { color: #ffffff; }
  .user-detail-modal .modal-icon {
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
  }

  /* Header bar layout in modal header */
  .user-detail-modal .header-bar { gap: 12px; }
  .user-detail-modal .header-left .user-avatar-large { margin-left: 0; }
  .user-detail-modal .identity .name { color: #ffffff; line-height: 1.15; }
  .user-detail-modal .identity .username { color: rgba(255,255,255,0.8); }
  .user-detail-modal .header-right .status-badge { background: rgba(255,255,255,0.1) !important; color: #fff !important; border-color: rgba(255,255,255,0.25) !important; }
  @media (max-width: 576px) {
    .user-detail-modal .modal-header { padding-right: 3rem; }
    .user-detail-modal .header-bar { flex-direction: column; align-items: flex-start !important; gap: 8px; }
  }

  .modal-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .user-detail {
    padding: 16px 0;
  }

  .user-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-color);
  }

  /* Avatar accent inside modal */
  .user-detail-modal .user-avatar-large {
    background: var(--accent);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }

  .user-info h4 {
    margin: 0 0 8px 0;
    color: var(--text-primary);
    font-size: 1.25rem;
  }

  .detail-section {
    margin-top: 16px;
  }

  .detail-section h6 {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 12px;
    font-size: 0.875rem;
  }

  .detail-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    background: var(--chip-bg);
    border: 1px solid var(--chip-border);
    border-radius: 10px;
    padding: 10px 12px;
  }

  .detail-item:last-child {
    margin-bottom: 0;
  }

  .detail-icon {
    color: var(--accent);
    margin-right: 8px;
    width: 14px;
  }

  .detail-link {
    color: var(--accent);
    text-decoration: none;
    font-size: 0.875rem;
  }

  .detail-link:hover {
    text-decoration: underline;
  }

  .detail-text {
    color: var(--text-muted);
    margin: 0;
    font-size: 0.875rem;
  }

  /* Username subtle text inside modal */
  .user-detail-modal .text-muted { color: var(--text-muted) !important; }

  /* Status badge refinement inside modal */
  .user-detail-modal .status-badge {
    border: 1px solid transparent;
    padding: 4px 10px;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: .2px;
  }
  .user-detail-modal .badge.bg-success.status-badge {
    background: rgba(16, 185, 129, 0.12) !important;
    color: #059669 !important;
    border-color: rgba(16, 185, 129, 0.28) !important;
  }
  .user-detail-modal .badge.bg-secondary.status-badge {
    background: rgba(100, 116, 139, 0.12) !important;
    color: var(--text-muted) !important;
    border-color: rgba(100, 116, 139, 0.28) !important;
  }

  /* SweetAlert Custom Styles */
  .swal2-popup {
    border-radius: 12px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  .swal2-title {
    color: #333;
    font-weight: 600;
    font-size: 1.125rem;
  }

  .swal2-content {
    color: #666;
    font-size: 0.875rem;
  }

  .swal2-confirm {
    border-radius: 6px;
    font-weight: 500;
    padding: 8px 16px;
    font-size: 0.875rem;
  }

  .swal2-cancel {
    border-radius: 6px;
    font-weight: 500;
    padding: 8px 16px;
    font-size: 0.875rem;
  }

  .swal2-success {
    border-color: #28a745;
  }

  .swal2-error {
    border-color: #dc3545;
  }

  .swal2-warning {
    border-color: #ffc107;
  }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
} 